import type { InvoiceDocument, InvoiceProfile, UpsertInvoiceProfileInput } from "@closer/shared";
import { upsertInvoiceProfileSchema } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  DefinitionGrid,
  FormField,
  Input,
  Modal,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * FATURALAMA — fatura profili, ödeme yöntemi, fatura belgeleri (CLOS-202 `[176]`).
 *
 * ### ⚠ Kart girişi formu BURADA YOK
 *
 * KARAR 03: *"kart girişi mümkün olduğunca sağlayıcının güvenli/tokenize yüzeyinde
 * yapılmalıdır."* İyzico entegrasyonu henüz KAPALI (`SaglayiciAcilmadi` — altı kabiliyet
 * doğrulanana kadar) — sahte bir kart formu çizmek, çalışmayan bir şeyi çalışıyormuş gibi
 * göstermek olurdu (bu depodaki en pahalı kusur sınıfı). Bu yüzden ödeme yöntemi bölümü
 * salt okunur bir liste + açık bir "şu an kullanılamıyor" durumu.
 */
export function BillingPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;

  return (
    <section className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg-heading sm:text-3xl">Faturalama</h1>
        <p className="text-sm text-fg-muted">Fatura profili, ödeme yöntemi ve fatura belgeleri.</p>
      </div>

      <InvoiceProfileCard tenantId={tenantId} />
      <PaymentMethodsCard tenantId={tenantId} />
      <DocumentsCard tenantId={tenantId} />
    </section>
  );
}

function InvoiceProfileCard({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "billing-profile"),
    queryFn: async () =>
      (await api<InvoiceProfile | null>("/api/billing/profile", { tenantId })).data,
  });

  const form = useZodForm(upsertInvoiceProfileSchema);

  if (sorgu.isPending) {
    return (
      <Card title="Fatura profili">
        <div role="status" aria-label="Fatura profili yükleniyor">
          <Skeleton lines={4} />
        </div>
      </Card>
    );
  }

  if (sorgu.error) {
    return (
      <Card title="Fatura profili">
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void sorgu.refetch()}
        />
      </Card>
    );
  }

  const mevcut = sorgu.data;

  const gonder = form.handleSubmit(async (girdi: UpsertInvoiceProfileInput) => {
    try {
      await api<InvoiceProfile>("/api/billing/profile", {
        method: "PUT",
        body: JSON.stringify({ ...girdi, expectedVersion: mevcut?.version }),
        tenantId,
      });
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "billing-profile") });
      toast.success("Fatura profili kaydedildi");
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields) {
        applyServerErrors(form, error);
        return;
      }
      /** ⚠ Sunucunun cümlesi: `409` "sayfayı yenileyin" diyor — iyimser eşzamanlılık çakışması. */
      toast.error(
        error instanceof ApiRequestError ? error.message : "Fatura profili kaydedilemedi.",
      );
    }
  });

  return (
    <Card title="Fatura profili">
      <form noValidate className="flex flex-col gap-3" onSubmit={gonder}>
        <FormField label="Fatura unvanı" error={form.formState.errors.legalName?.message} required>
          <Input defaultValue={mevcut?.legalName ?? ""} {...form.register("legalName")} />
        </FormField>
        <FormField
          label="Vergi/T.C. kimlik numarası"
          error={form.formState.errors.taxIdOrNationalId?.message}
          required
        >
          <Input
            defaultValue={mevcut?.taxIdOrNationalId ?? ""}
            {...form.register("taxIdOrNationalId")}
          />
        </FormField>
        <FormField label="Adres" error={form.formState.errors.addressLine1?.message} required>
          <Input defaultValue={mevcut?.addressLine1 ?? ""} {...form.register("addressLine1")} />
        </FormField>
        <FormField label="Adres (devamı)" error={form.formState.errors.addressLine2?.message}>
          <Input defaultValue={mevcut?.addressLine2 ?? ""} {...form.register("addressLine2")} />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Şehir" error={form.formState.errors.city?.message} required>
            <Input defaultValue={mevcut?.city ?? ""} {...form.register("city")} />
          </FormField>
          <FormField
            label="Ülke (ISO — ör. TR)"
            error={form.formState.errors.country?.message}
            required
          >
            <Input defaultValue={mevcut?.country ?? ""} {...form.register("country")} />
          </FormField>
        </div>
        <FormField label="Posta kodu" error={form.formState.errors.postalCode?.message} required>
          <Input defaultValue={mevcut?.postalCode ?? ""} {...form.register("postalCode")} />
        </FormField>
        <FormField label="Fatura e-postası" error={form.formState.errors.email?.message} required>
          <Input type="email" defaultValue={mevcut?.email ?? ""} {...form.register("email")} />
        </FormField>
        <FormField label="Telefon" error={form.formState.errors.phone?.message}>
          <Input defaultValue={mevcut?.phone ?? ""} {...form.register("phone")} />
        </FormField>
        <div className="flex justify-end">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            Kaydet
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PaymentMethodsCard({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "payment-methods"),
    queryFn: async () =>
      (
        await api<
          { id: string; brand: string; last4: string; expiryMonth: number; expiryYear: number }[]
        >("/api/billing/payment-methods", { tenantId })
      ).data,
  });

  /**
   * ÖDEME YÖNTEMİ KALDIRMA (CLOS-1109 · `DELETE /api/billing/payment-methods/:id`).
   *
   * ### ⚠ Uç yazılmıştı, çağıran yoktu
   *
   * Ekran yalnız listeliyordu: eklenen bir kartı KALDIRMANIN ürün içinde hiçbir yolu
   * yoktu. Denetimin "oluşturma bağlı, düzeltme bağlı değil" deseni — ve ödeme aracında
   * bu desen özellikle pahalı, çünkü kullanıcı silemediği bir aracı iptal etmek için
   * destekle konuşmak zorunda kalır.
   *
   * ⚠ Kapı `subscription.payment` — UCUN kapısıyla aynı. Farklı bir anahtar seçseydik
   * düğmesi görünüp basınca 403 alınan bir yüzey doğardı.
   */
  const kaldir = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/billing/payment-methods/${id}`, { method: "DELETE", tenantId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "payment-methods") });
      toast.success("Ödeme yöntemi kaldırıldı");
    },
    /** ⚠ Sunucunun cümlesi: "son ödeme yöntemi kaldırılamaz" gibi bir ret kendi nedenini taşır. */
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  return (
    <Card title="Ödeme yöntemi">
      {sorgu.isPending ? (
        <div role="status" aria-label="Ödeme yöntemleri yükleniyor">
          <Skeleton lines={2} />
        </div>
      ) : sorgu.error ? (
        /**
         * ⚠ Liste düştüğünde "kayıtlı yöntem yok" demek kullanıcıyı ikinci bir kart
         * eklemeye iter; sağlayıcı açıldığında bu mükerrer ödeme aracı demektir.
         */
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sorgu.data && sorgu.data.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {sorgu.data.map((y) => (
            <li key={y.id} className="flex items-center gap-2 text-sm text-fg">
              <span className="font-medium">{y.brand}</span>
              <span className="tabular-nums text-fg-muted">
                •••• {y.last4} — {y.expiryMonth}/{y.expiryYear}
              </span>
              {can("subscription.payment") ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  loading={kaldir.isPending}
                  onClick={() => kaldir.mutate(y.id)}
                >
                  Kaldır
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p data-testid="odeme-yontemi-yok" className="text-sm text-fg-muted">
          Kayıtlı ödeme yöntemi yok. Kart ekleme, sağlayıcı entegrasyonu tamamlanınca burada
          açılacak.
        </p>
      )}
    </Card>
  );
}

function DocumentsCard({ tenantId }: { tenantId: string }) {
  const { preferences } = usePreferences();
  const toast = useToast();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "billing-documents"),
    queryFn: async () =>
      (await api<InvoiceDocument[]>("/api/billing/documents", { tenantId })).data,
  });

  const [ayrinti, setAyrinti] = useState<InvoiceDocument | null>(null);

  /**
   * BELGE AÇMA — İKİ ADIM, TEK MUTATION (CLOS-1117 · yetim uç D).
   *
   * ### ⚠ `window.open` KALKTI
   *
   * Kart eskiden bileti alıp `window.open(bilet.downloadUrl)` diyordu. `downloadUrl` bir
   * DOSYA değil: `GET /api/billing/documents/download?token=` bileti çözüyor, yetkiyi ve
   * kiracıyı YENİDEN doğruluyor, indirmeyi audit'e yazıyor ve `InvoiceDocument` **JSON**
   * döndürüyor (`lib/payments/documents.ts` — PDF ya da depolama nesnesi YOK). Yani
   * kullanıcı yeni sekmede ham JSON görüyordu.
   *
   * ⚠ Ucun "yetim" görünmesinin sebebi de buydu: URL sunucuda üretildiği için
   * `apps/web/src` içinde hiçbir yerde geçmiyordu. Bileti alıp ucu `api()` ile çağırmak
   * hem statik bağı kuruyor hem de JSON-sekme kusurunu kaldırıyor.
   *
   * ⚠ İKİNCİ ÇAĞRI ATLANAMAZ: audit satırı (`billing.invoice_document_downloaded`)
   * yalnız bozdurma ucunda yazılıyor; listedeki satırı doğrudan göstermek "belge açıldı"
   * kaydını sessizce düşürürdü.
   */
  const belgeAc = useMutation({
    mutationFn: async (documentId: string) => {
      const bilet = (
        await api<{ downloadUrl: string; expiresInSeconds: number }>(
          `/api/billing/documents/${documentId}/download`,
          { tenantId },
        )
      ).data;
      return (await api<InvoiceDocument>(bilet.downloadUrl, { tenantId })).data;
    },
    onSuccess: (belge) => setAyrinti(belge),
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Belge açılamadı.");
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Fatura belgeleri">
        <div role="status" aria-label="Fatura belgeleri yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  /**
   * ⚠ ÖNCE HATA, SONRA BOŞLUK (CLOS-966).
   *
   * Kart eskiden doğrudan `!sorgu.data` diye soruyordu; sorgu DÜŞTÜĞÜNDE de `data`
   * `undefined` olduğu için ekranda *"Henüz bir fatura veya makbuz yok."* yazıyordu.
   * Kesilmiş bir fatura, bir arıza yüzünden hiç yokmuş gibi görünüyordu — muhasebe
   * kaydının sessizce kaybolması. Aynı sayfanın profil kartı hata dalını zaten okuyordu;
   * asimetri buradaydı. Veri yokluğu ancak sorgu BAŞARILIYKEN iddia edilebilir.
   */
  if (sorgu.error) {
    return (
      <Card title="Fatura belgeleri">
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void sorgu.refetch()}
        />
      </Card>
    );
  }

  if (!sorgu.data || sorgu.data.length === 0) {
    return (
      <Card title="Fatura belgeleri">
        <p data-testid="belge-yok" className="text-sm text-fg-muted">
          Henüz bir fatura veya makbuz yok.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Fatura belgeleri">
      <ul className="flex flex-col gap-3">
        {sorgu.data.map((belge) => (
          <li
            key={belge.id}
            data-testid="belge-satiri"
            className="flex items-center justify-between gap-3 text-sm"
          >
            <div className="flex flex-col">
              <span className="font-medium text-fg-strong">
                {belgeTuruAdi(belge.type)}
                {belge.voided ? <Badge tone="warning">Geçersiz kılındı</Badge> : null}
              </span>
              <span className="text-fg-muted">
                {formatDate(belge.periodStart, preferences.locale)} —{" "}
                {formatDate(belge.periodEnd, preferences.locale)} ·{" "}
                {(belge.amountMinorUnits / 100).toLocaleString(preferences.locale, {
                  style: "currency",
                  currency: belge.currency,
                })}
              </span>
            </div>
            <Button
              variant="secondary"
              onClick={() => belgeAc.mutate(belge.id)}
              disabled={belgeAc.isPending}
            >
              Belgeyi aç
            </Button>
          </li>
        ))}
      </ul>

      <Modal
        open={ayrinti !== null}
        onClose={() => setAyrinti(null)}
        title="Belge ayrıntısı"
        size="sm"
        footer={
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={() => setAyrinti(null)}>
              Kapat
            </Button>
          </div>
        }
      >
        {ayrinti ? (
          <DefinitionGrid
            items={[
              { key: "tur", label: "Tür", value: belgeTuruAdi(ayrinti.type) },
              {
                key: "donem",
                label: "Dönem",
                value: `${formatDate(ayrinti.periodStart, preferences.locale)} — ${formatDate(
                  ayrinti.periodEnd,
                  preferences.locale,
                )}`,
              },
              {
                key: "tutar",
                label: "Tutar",
                value: (ayrinti.amountMinorUnits / 100).toLocaleString(preferences.locale, {
                  style: "currency",
                  currency: ayrinti.currency,
                }),
              },
              {
                key: "kdv",
                label: "KDV",
                value: (ayrinti.taxAmountMinorUnits / 100).toLocaleString(preferences.locale, {
                  style: "currency",
                  currency: ayrinti.currency,
                }),
              },
              { key: "para-birimi", label: "Para birimi", value: ayrinti.currency },
              {
                key: "olusturulma",
                label: "Oluşturulma",
                value: formatDate(ayrinti.createdAt, preferences.locale),
              },
              {
                key: "gecersiz",
                label: "Geçersiz kılındı mı",
                value: ayrinti.voided ? "Evet" : "Hayır",
              },
            ]}
          />
        ) : null}
      </Modal>
    </Card>
  );
}

/** Belge türünün kullanıcıya görünen adı — satır ve ayrıntı aynı sözlükten okuyor. */
function belgeTuruAdi(type: InvoiceDocument["type"]): string {
  if (type === "invoice") return "Fatura";
  if (type === "receipt") return "Makbuz";
  return "Kredi belgesi";
}
